import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { IMAGE_CONSTANTS } from '@/constants';

import type { TaskDetail, TaskTabType } from '@/types';

interface TaskActionButtonsProps {
  tab: TaskTabType;
  task: TaskDetail;
  isOperating?: boolean | undefined;
  onAccept?: (() => void) | undefined;
  onStart?: (() => void) | undefined;
  onCancel?: (() => void) | undefined;
  onView?: (() => void) | undefined;
}

/**
 * Icon-based action button matching Xamarin design.
 * Xamarin: SVG icon (HeightRequest=35) + Label (PoppinsRegular, FontSize=12, TaskCardMobileFrameColor #7F98B0)
 */
function IconActionButton({
  icon,
  label,
  onClick,
  disabled,
  isLoading,
}: {
  icon: string;
  label: string;
  onClick?: (() => void) | undefined;
  disabled?: boolean | undefined;
  isLoading?: boolean | undefined;
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      disabled={disabled ?? isLoading}
      className="flex flex-col items-center justify-center min-w-[50px] disabled:opacity-50"
    >
      {isLoading ? (
        <Loader2 className="h-[35px] w-[35px] animate-spin text-task-frame" />
      ) : (
        <img src={icon} alt={label} className="h-[35px] w-[35px] object-contain" />
      )}
      <span className="text-xs font-poppins text-task-frame mt-0.5">{label}</span>
    </button>
  );
}

export function TaskActionButtons({
  tab,
  isOperating,
  onAccept,
  onStart,
  onCancel,
}: TaskActionButtonsProps) {
  const { t } = useTranslation();
  switch (tab) {
    // ── MY TASKS (SelectedIndex=0) ──
    // Xamarin: Cancel icon (ic_task_cancel) + Start icon (start_task)
    case 'myTasks':
      return (
        <div className="flex items-center gap-3">
          {onCancel && (
            <IconActionButton
              icon={IMAGE_CONSTANTS.taskCancelIcon}
              label={t('common.cancel')}
              onClick={onCancel}
              disabled={isOperating}
            />
          )}
          {onStart && (
            <IconActionButton
              icon={IMAGE_CONSTANTS.startTask}
              label={t('common.start')}
              onClick={onStart}
              disabled={isOperating}
              isLoading={isOperating}
            />
          )}
        </div>
      );

    // ── UPCOMING (SelectedIndex=1) ──
    // Xamarin: Start icon (start_task) with StartCommand + Accept icon (green_check) with AcceptOrStartCommand
    case 'upcoming':
      return (
        <div className="flex items-center gap-3">
          {onStart && (
            <IconActionButton
              icon={IMAGE_CONSTANTS.startTask}
              label={t('common.start')}
              onClick={onStart}
              disabled={isOperating}
            />
          )}
          {onAccept && (
            <IconActionButton
              icon={IMAGE_CONSTANTS.greenCheck}
              label={t('common.accept')}
              onClick={onAccept}
              disabled={isOperating}
              isLoading={isOperating}
            />
          )}
        </div>
      );

    // ── COMPLETED (SelectedIndex=2) ──
    // Xamarin: No action buttons visible on completed tab
    case 'completed':
      return null;

    default:
      return null;
  }
}
